/* eslint-disable import/no-cycle */
/* eslint @typescript-eslint/no-explicit-any: 0 */
import {
    CustomerPortal,
    CustomerPortalQuote,
    underwritingRequirementType,
    UnderwritingRequirementUpdate,
    PaymentInformation,
} from '../Agent/types';
import {CustomerPortalData} from '../CaseManager/types';
// TODO fix this dependency cycle
// eslint-disable-next-line import/no-cycle
import API from './API';
import {ClientQuoteData} from './CRM';

type GraphQLError = {
    locations: {line: number; column: number}[];
    message: string;
    path: string[];
};

export class DeepThoughtError extends Error {
    constructor(public errors?: GraphQLError[]) {
        super('An error occurred requesting to Deep Thought');
    }
}

type DeepThoughtResponse<Operation extends string, Data = any> = {
    data?: Record<Operation, Data>;
    errors?: GraphQLError[];
};

export type Identification = {
    id: string;
    type: string;
    value: string;
    updated_at: string;
};

type Event = {
    id: string;
    type: string;
    date: string;
    updated_at: string;
};

export type Document = {
    id: string;
    name: string;
    filename: string;
    s3key: string;
    downloadUrl: string;
    uploadUrl: string;
};

type RequirementParams = {
    status?: string;
    type?: string;
};

export const PaperworkStatusType = {
    INCOMPLETE: 'incomplete',
    CONFIRMED: 'confirmed',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PaperworkStatusType = (typeof PaperworkStatusType)[keyof typeof PaperworkStatusType];

export const PaperworkFormSpecType = {
    BENEFICIARIES: 'beneficiaries',
    ADDRESS: 'address',
    ADDITIONAL_HEALTH: 'additionalHealth',
    EMPLOYMENT: 'employment',
    BIRTH_STATUS: 'birthStatus',
    ADDITIONAL_CONDITIONS: 'additionalConditions',
    ADDITIONAL_POLICY_INTEREST: 'additionalPolicyInterests',
    FINANCIAL_SUITABILITY: 'financialSuitability',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PaperworkFormSpecType = (typeof PaperworkFormSpecType)[keyof typeof PaperworkFormSpecType];

export type Paperwork = {
    id: string;
    completed_paperwork: string;
    status: PaperworkStatusType;
    form_spec: {
        label: PaperworkFormSpecType;
    };
    updatedAt: string;
};

export type Customer = {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    email: string;
    consent: boolean;
    identifications: Identification[];
    events: Event[];
    requirements: underwritingRequirementType[];
    paperworks: Paperwork[];
    paymentInformations: PaymentInformation[];
};

export type Requirement<Type = string, Status = string, DataJson = any> = {
    id: string;
    type: Type;
    status: Status;
    title: string;
    link?: string;
    additionalInstructions?: string;
    internalNote?: string;
    fileTitle?: string;
    data?: string;
    dataJson?: DataJson;
    private?: boolean;
    customerId: string;
    policyId?: string;
    paymentInformationId?: string;
    customer?: Customer;
    documents?: Document[];
};

export const PolicyRequirementType = {
    POLICY_DOCUSIGN: 'policyDocusign',
} as const;

export type DocusignEnvelope = {
    id: string;
    envelopeId: string;
    redirectUrl: string;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyRequirementType = (typeof PolicyRequirementType)[keyof typeof PolicyRequirementType];

export const PolicyRequirementStatus = {
    OUTSTANDING: 'outstanding',
    RECEIVED: 'received',
    REJECTED: 'rejected',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyRequirementStatus = (typeof PolicyRequirementStatus)[keyof typeof PolicyRequirementStatus];

export type PolicyRequirement = Requirement<PolicyRequirementType, PolicyRequirementStatus>;

export const PolicyType = {
    TERM: 'Term',
    IUL: 'IUL',
    GUARANTEED: 'Guaranteed',
    VUL: 'VUL',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyType = (typeof PolicyType)[keyof typeof PolicyType];

export const PolicyStatus = {
    ACTIVE: 'active',
    DRAFT: 'draft',
    DELETED: 'deleted',
    CANCELLED: 'cancelled',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PolicyStatus = (typeof PolicyStatus)[keyof typeof PolicyStatus];

export const Carrier = {
    AMERITAS: 'Ameritas',
    NATIONAL_LIFE_GROUP: 'National Life Group',
    NORTH_AMERICAN: 'North American',
    PACIFIC_LIFE: 'Pacific Life',
    GERBER: 'Gerber',
    GREAT_WESTERN: 'Great Western',
    MUTUAL_OF_OMAHA: 'Mutual of Omaha',
    TRANSAMERICA: 'Transamerica',
    ALLIANZ: 'Allianz',
    ASSURITY: 'Assurity',
    PRINCIPAL: 'Principal',
    PROTECTIVE: 'Protective',
    PRUDENTIAL: 'Prudential',
    UNITED_HOME_LIFE: 'United Home Life',
    VIVA_LIFE: 'Viva Life',
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Carrier = (typeof Carrier)[keyof typeof Carrier];

export type Policy = {
    id: string;
    customerId: string;
    label: string;
    type: PolicyType;
    status: PolicyStatus;
    number: string;
    carrierName: Carrier;
    coverageAmount: string;
    monthlyContribution: string;
    effectiveDate: string;
    documents: Document[];
    documentIds?: string[];
    requirements: Requirement[];
};

const createPaymentInfoKeys = [
    'label',
    'routingNumber',
    'accountNumber',
    'paymentFrequency',
    'dayOfMonth',
    'plaidAccountId',
    'ownerName',
    'institutionName',
    'accountType',
    'conditionalCoverage',
];

const REQUIREMENT_FIELDS = [
    'id',
    'type',
    'status',
    'title',
    'link',
    'additionalInstructions',
    'internalNote',
    'fileTitle',
    'data',
    'dataJson',
    'customerId',
    'policyId',
    'paymentInformationId',
    'documents { id name s3key downloadUrl uploadUrl }',
];

/* prettier-ignore */
const DOCUMENT_FIELDS = [
    'id',
    'name',
    'filename',
    's3key',
    'downloadUrl',
    'uploadUrl',
];

const POLICY_FIELDS = [
    'id',
    'customerId',
    'type',
    'label',
    'status',
    'carrierName',
    'number',
    'coverageAmount',
    'monthlyContribution',
    'effectiveDate',
    {documents: DOCUMENT_FIELDS},
    {requirements: REQUIREMENT_FIELDS},
];

export default class CustomerPortalAPI {
    public static async GetCustomerPortalData(email: null | string) {
        if (email == null || email.length === 0) {
            return null;
        }
        const body = {
            operation: 'customerByEmail',
            variables: {
                email: email,
            },
            fields: [
                'id',
                'firstName',
                'lastName',
                'dateOfBirth',
                'email',
                'consent',
                'identifications {id type value updated_at}',
                'events {id type date updated_at}',
                'requirements {id type status data data_json updatedAt}',
                'paperworks {id status completed_paperwork updatedAt form_spec{label}}',
                'paymentInformations {id label routingNumber accountNumber paymentFrequency dayOfMonth plaidAccountId updatedAt ownerName institutionName accountType conditionalCoverage}',
            ],
        };

        const data = await API.Call<{data: CustomerPortalData}>('customer-portal-agent', 'POST', body);

        return data?.data;
    }

    public static async GetCustomerById(id: string) {
        if (id.length == 0) {
            return null;
        }

        const body = {
            operation: 'customerById',
            variables: {
                id: {
                    type: 'ID',
                    value: id,
                },
            },
            fields: ['id', 'firstName', 'lastName', 'dateOfBirth', 'email', 'phoneNumber'],
        };

        const data = await API.Call<{data: {error?: object; errors?: object[]; customerById: CustomerPortal}}>(
            'customer-portal-agent',
            'POST',
            body,
        );

        if (!data) {
            throw new Error('Failed to fetch customer');
        }

        return data.data;
    }

    public static async GetCustomerRequirements(customerId?: string, params?: RequirementParams) {
        if (!customerId || customerId.length === 0) {
            return null;
        }

        const body = {
            operation: 'requirements',
            variables: {
                customerId: customerId,
                ...params,
            },
            fields: [
                'id', // this is the same id passed as above
                'type',
                'status',
                'title',
                'link',
                'additional_instructions',
                'internal_note',
                'file_title',
                'data',
                'documents {name downloadUrl}',
                'private',
                'data_json',
                'paymentInformationId',
                'updatedAt',
                'insertedAt',
            ],
        };

        const data = await API.Call<{
            data: {requirements: underwritingRequirementType[]};
        }>('customer-portal-agent', 'POST', body);

        if (!data) {
            throw new Error('Failed to fetch customer');
        }

        return data.data;
    }

    public static async GetCustomerGeneralPaymentInfo(customerId?: string) {
        if (!customerId || customerId.length === 0) {
            return null;
        }

        const body = {
            operation: 'generalPaymentInformation',
            variables: {
                customerId: customerId,
            },
            fields: [
                'id',
                'label',
                'routingNumber',
                'accountNumber',
                'updatedAt',
                'startDate',
                'paymentFrequency',
                'dayOfMonth',
                'plaidAccountId',
                'ownerName',
                'institutionName',
                'conditionalCoverage',
                'accountType',
            ],
        };

        const data = await API.Call<{data: {generalPaymentInformation: PaymentInformation}}>(
            'customer-portal-agent',
            'POST',
            body,
        );

        if (!data) {
            throw new Error('Failed to fetch customer');
        }

        return data.data.generalPaymentInformation;
    }

    public static async GetCustomerPaymentInfo(customerId?: string) {
        if (!customerId || customerId.length === 0) {
            return null;
        }

        const body = {
            operation: 'paymentInformations',
            variables: {
                customerId: customerId,
            },
            fields: [
                'id',
                'label',
                'routingNumber',
                'accountNumber',
                'updatedAt',
                'startDate',
                'paymentFrequency',
                'dayOfMonth',
                'plaidAccountId',
                'ownerName',
                'institutionName',
                'conditionalCoverage',
                'accountType',
            ],
        };

        const data = await API.Call<{data: {paymentInformations: PaymentInformation[]}}>(
            'customer-portal-agent',
            'POST',
            body,
        );

        if (!data) {
            throw new Error('Failed to fetch customer payment info');
        }

        return data.data.paymentInformations;
    }

    public static async UpdateUnderwritingRequirements({
        customerId,
        newRequirements,
    }: {
        customerId?: string;
        newRequirements: underwritingRequirementType[] | UnderwritingRequirementUpdate[];
    }): Promise<DocusignEnvelope | null> {
        if (!customerId || customerId.length === 0 || !newRequirements || newRequirements.length === 0) {
            return null;
        }

        const body = {
            data: newRequirements,
            customerId,
        };

        const data = await API.Call<DocusignEnvelope>('underwriting-requirements', 'POST', body);
        return data;
    }

    public static async SendDocusignEnvelope(envelopeId: string, lockToken?: string | null) {
        const body = {
            envelopeId,
            lockToken,
        };
        const data = await API.Call('underwriting-requirements/send-envelope', 'POST', body);
        return data;
    }

    public static async UpdateCustomerPaymentInformation(
        customerId: string,
        newPaymentInformation: PaymentInformation,
    ) {
        if (!customerId || customerId.length === 0 || !newPaymentInformation) {
            return null;
        }

        const paymentInfo = {...newPaymentInformation};

        if (paymentInfo.index) delete paymentInfo.index;

        const variables: any = {
            customerId: customerId,
            paymentInformationId: paymentInfo.id,
            routingNumber: paymentInfo.routingNumber || '',
            accountNumber: paymentInfo.accountNumber || '',
            paymentFrequency: paymentInfo.paymentFrequency || '',
            plaidAccountId: paymentInfo.plaidAccountId || '',
            ownerName: paymentInfo.ownerName || '',
            institutionName: paymentInfo.institutionName || '',
            conditionalCoverage: !!paymentInfo.conditionalCoverage,
            accountType: paymentInfo.accountType || '',
        };

        // we can't nullify field, so exclude from operation if not explicitly set
        if (paymentInfo.dayOfMonth) variables.dayOfMonth = paymentInfo.dayOfMonth;

        const body = {
            operation: 'updatePaymentInformation',
            variables: {
                ...variables,
            },
            fields: [
                'id',
                'label',
                'routingNumber',
                'accountNumber',
                'updatedAt',
                'startDate',
                'paymentFrequency',
                'dayOfMonth',
                'plaidAccountId',
                'ownerName',
                'institutionName',
                'conditionalCoverage',
                'accountType',
            ],
        };

        const data = await API.Call<{data: {updatePaymentInformation: PaymentInformation}}>(
            'customer-portal-agent',
            'POST',
            body,
        );

        if (!data) {
            throw new Error('Failed to fetch customer');
        }

        return data.data.updatePaymentInformation;
    }

    public static async CreateCustomerPaymentInformation(
        customerId: string,
        newPaymentInformation: PaymentInformation,
    ) {
        if (!customerId || customerId.length === 0 || !newPaymentInformation) {
            return null;
        }

        const paymentInfo = {...newPaymentInformation};

        const variables: any = {};
        createPaymentInfoKeys.reduce((carry, k) => {
            if (paymentInfo[k as keyof PaymentInformation]) {
                /* eslint-disable no-param-reassign */
                carry[k] = paymentInfo[k as keyof PaymentInformation];
            }
            return carry;
        }, variables);

        variables.customerId = customerId;

        const body = {
            operation: 'createPaymentInformation',
            variables: {
                ...variables,
            },
            fields: [
                'id',
                'label',
                'routingNumber',
                'accountNumber',
                'updatedAt',
                'startDate',
                'paymentFrequency',
                'dayOfMonth',
                'plaidAccountId',
                'ownerName',
                'institutionName',
                'conditionalCoverage',
                'accountType',
            ],
        };

        const data = await API.Call<{data: {createPaymentInformation: PaymentInformation}}>(
            'customer-portal-agent',
            'POST',
            body,
        );

        if (!data) {
            throw new Error('Failed to fetch customer');
        }

        return data.data.createPaymentInformation;
    }

    public static async createCustomer(customer: CustomerPortal) {
        if (!customer) {
            return;
        }

        const body = {
            operation: 'createCustomer',
            variables: customer,
            fields: ['id', 'firstName', 'lastName', 'dateOfBirth', 'email', 'phoneNumber', 'pipelineStep'],
        };

        const data = await API.Call<{errors: object[]; data: {createCustomer?: CustomerPortal}}>(
            'customer-portal-agent',
            'POST',
            body,
        );
        return data;
    }

    public static async createQuote(quoteBody: CustomerPortalQuote) {
        if (!quoteBody.email && !quoteBody.quote) {
            throw new Error('Should contain email & body');
        }
        const data = await API.Call<{errors: object[]}>('customer-portal/create-quote', 'POST', quoteBody);
        return data;
    }

    public static async searchCustomers(keyword: string) {
        if (!keyword) {
            return null;
        }

        const body = {
            operation: 'searchCustomers',
            variables: {
                email: keyword,
                firstName: keyword,
                lastName: keyword,
                phoneNumber: keyword,
            },
            fields: [
                'id',
                'firstName',
                'lastName',
                'dateOfBirth',
                'email',
                'phoneNumber',
                'pipelineStep',
                'paymentInformations {id label routingNumber accountNumber paymentFrequency dayOfMonth plaidAccountId updatedAt ownerName institutionName accountType conditionalCoverage}',
            ],
        };

        const data = await API.Call<{data: {searchCustomers: CustomerPortal[]}}>('customer-portal-agent', 'POST', body);
        return data;
    }

    public static async getPoliciesForCustomer({customerId}: {customerId: string}) {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'policies',
            variables: {
                customerId,
            },
            fields: POLICY_FIELDS,
        }) as DeepThoughtResponse<'policies', Policy[]>;
    }

    public static async getPolicyForCustomer({customerId, policyId}: {customerId: string; policyId: string}) {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'policy',
            variables: {
                id: policyId,
                customerId,
            },
            fields: POLICY_FIELDS,
        }) as DeepThoughtResponse<'policy', Policy>;
    }

    public static async createPolicyForCustomer(data: Partial<Omit<Policy, 'id' | 'documents'>>) {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'createPolicy',
            variables: data,
            fields: POLICY_FIELDS,
        }) as Promise<DeepThoughtResponse<'createPolicy', Policy>>;
    }

    public static async updatePolicyForCustomer(
        data: Partial<Omit<Policy, 'documents'>>,
    ): Promise<DeepThoughtResponse<'updatePolicy', Policy> | null> {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'updatePolicy',
            variables: data,
            fields: POLICY_FIELDS,
        });
    }

    public static async updateRequirementForCustomer(
        requirement: Partial<Requirement>,
    ): Promise<DeepThoughtResponse<'updateRequirement', Requirement> | null> {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'updateRequirement',
            variables: requirement,
            fields: REQUIREMENT_FIELDS,
        });
    }

    public static async createRequirementForCustomer(
        requirement: Partial<Requirement>,
    ): Promise<DeepThoughtResponse<'createRequirement', Requirement> | null> {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'createRequirement',
            variables: requirement,
            fields: REQUIREMENT_FIELDS,
        });
    }

    public static async createDocument(
        document: Partial<Pick<Document, 'name' | 's3key'>>,
    ): Promise<DeepThoughtResponse<'createDocument', Document> | null> {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'createDocument',
            variables: document,
            fields: DOCUMENT_FIELDS,
        });
    }

    public static async updateDocument(
        document: Partial<Pick<Document, 'id' | 'name' | 's3key'>>,
    ): Promise<DeepThoughtResponse<'updateDocument', Document> | null> {
        return API.Call('customer-portal-agent', 'POST', {
            operation: 'updateDocument',
            variables: document,
            fields: DOCUMENT_FIELDS,
        });
    }

    public static async GetCustomerQuotes(email: string): Promise<ClientQuoteData[] | null> {
        const ret = await API.Call<ClientQuoteData[]>(`customer-portal-agent/quote/${email}`, 'GET');
        return ret;
    }
}
